import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { AuthStateFacade, PositionsFacade, StoreErrorHandler } from '@zero/shared/util-global-state';
import { catchError, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';
import { StatisticsActions, StatisticsApiActions } from './statistics.actions';

import { Store } from '@ngrx/store';

import { STATS_DIVIDENDS_FORECAST_YEARS } from 'constants/shared-constants';
import { Dividend, DividendPeriod } from '../entities/dividends.entity';
import { StatisticsInfrastructure } from '../infrastructure/statistics.infrastructure';
import { statisticsFeature } from './statistics.reducer';

@Injectable()
export class StatisticsEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _statisticsInfrastructure = inject(StatisticsInfrastructure);
  private readonly _authStateFacade = inject(AuthStateFacade);
  private readonly _positionsFacade = inject(PositionsFacade);
  private readonly _store = inject(Store);
  private readonly _storeErrorHandler = inject(StoreErrorHandler);

  public loadTradeTicker$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.loadTradeTicker),
      filter(() => !!this._authStateFacade.customerId),
      switchMap(() =>
        this._statisticsInfrastructure.getTradeTicker().pipe(
          map((trades) => StatisticsApiActions.loadTradeTickerSuccess({ trades })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadTradeTickerFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadTradeTickerFailure({ error }))),
    );
  });

  public subscribeToTradeTicks$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.subscribeToTradeTicks),
      switchMap(() =>
        this._statisticsInfrastructure.listenToTradeTicks().pipe(
          map((trade) => StatisticsActions.pushTradeTick({ trade })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadTradeTickerFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadTradeTickerFailure({ error }))),
    );
  });

  public unsubscribeFromTradeTicks$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(StatisticsActions.unsubscribeFromTradeTicks),
        switchMap(() => this._statisticsInfrastructure.unsubscribeFromTradeTicks()),
      );
    },
    { dispatch: false },
  );

  public subscribeToPerformance$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.subscribeToPerformance),
      tap(() => this._positionsFacade.loadPositions()),
      tap(() => this._positionsFacade.subscribeToPositionQuotes()),
      switchMap(() =>
        this._positionsFacade.portfolioPerformance$.pipe(
          map((performance) => StatisticsActions.pushPerformance({ performance })),
          catchError(() => of(StatisticsActions.subscribeToTradeTicksFailure())),
        ),
      ),
      catchError(() => of(StatisticsActions.subscribeToTradeTicksFailure())),
    );
  });

  public loadMyTrades$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.loadMyTrades),
      filter(() => !!this._authStateFacade.customerId),
      switchMap(() =>
        this._statisticsInfrastructure.loadMyTrades().pipe(
          map((myTrades) => StatisticsApiActions.loadMyTradesSuccess({ myTrades })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadMyTradesFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadMyTradesFailure({ error }))),
    );
  });

  public loadWeightingByAssetType$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.loadWeightingByAssetType),
      filter(() => !!this._authStateFacade.customerId),
      switchMap(() =>
        this._statisticsInfrastructure.loadWeightingByAssetType().pipe(
          map((weightingByAssetType) => StatisticsApiActions.loadWeightingByAssetTypeSuccess({ weightingByAssetType })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadWeightingByAssetTypeFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadWeightingByAssetTypeFailure({ error }))),
    );
  });

  public loadWeightingByTopPositions$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.loadWeightingByTopPositions),
      filter(() => !!this._authStateFacade.customerId),
      switchMap(() =>
        this._statisticsInfrastructure.loadWeightingByTopPositions().pipe(
          map((weightingByTopPositions) => StatisticsApiActions.loadWeightingByTopPositionsSuccess({ weightingByTopPositions })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadWeightingByTopPositionsFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadWeightingByTopPositionsFailure({ error }))),
    );
  });

  public loadWeightingByPositions$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.loadWeightingByPositions),
      filter(() => !!this._authStateFacade.customerId),
      switchMap(() =>
        this._statisticsInfrastructure.loadWeightingByPositions().pipe(
          map((weightingByPositions) => StatisticsApiActions.loadWeightingByPositionsSuccess({ weightingByPositions })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadWeightingByPositionsFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadWeightingByPositionsFailure({ error }))),
    );
  });

  public loadMyNews$ = createEffect(() => {
    const compareArrays = (prev: string[], curr: string[]) =>
      prev.length === curr.length && prev.every((element, index) => element === curr[index]);
    return this._actions$.pipe(
      ofType(StatisticsActions.loadMyNews),
      switchMap(() =>
        this._positionsFacade.positionStocksISINs$.pipe(
          filter((isins) => !!isins?.length),
          distinctUntilChanged(compareArrays),
          // eslint-disable-next-line @ngrx/no-dispatch-in-effects
          tap(() => this._store.dispatch(StatisticsActions.setNewsLoading({ loading: true }))),
          switchMap((isins) => this._statisticsInfrastructure.loadMyNews(isins)),
          map((news) => StatisticsApiActions.loadMyNewsSuccess({ news })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadMyNewsFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadMyNewsFailure({ error }))),
    );
  });

  public loadChartAndStats$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.loadChartAndStats),
      filter(() => !!this._authStateFacade.customerId),
      concatLatestFrom(() => this._store.select(statisticsFeature.selectChartAndStatsInitiallyLoaded)),
      filter(([{ force }, loaded]) => !loaded || force),
      // eslint-disable-next-line @ngrx/no-dispatch-in-effects
      tap(() => this._store.dispatch(StatisticsActions.setChartsAndStatsLoading({ loading: true }))),
      switchMap(() =>
        this._statisticsInfrastructure.getChartAndStats().pipe(
          map((chartAndStats) => StatisticsApiActions.loadChartAndStatsSuccess({ chartAndStats })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadChartAndStatsFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadChartAndStatsFailure({ error }))),
    );
  });

  public loadDividends$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.loadDividends),
      filter(() => !!this._authStateFacade.customerId),
      switchMap(() =>
        this._statisticsInfrastructure.loadDividends().pipe(
          tap((dividends) => this.appendSelfcalculatedForecast(dividends)),
          map((dividends) => StatisticsApiActions.loadDividendsSuccess({ dividends: this.sortInstrumentNameAsc(dividends) })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadDividendsFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadDividendsFailure({ error }))),
    );
  });

  public loadDividendYields$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.loadDividendYields),
      filter(() => !!this._authStateFacade.customerId),
      concatLatestFrom(() => this._store.select(statisticsFeature.selectDividendYieldsInitiallyLoaded)),
      filter(([{ force }, loaded]) => !loaded || force),
      switchMap(() =>
        this._statisticsInfrastructure.loadDividendYields().pipe(
          map((dividendYields) => StatisticsApiActions.loadDividendYieldsSuccess({ dividendYields })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadDividendYieldsFailure({ error }))),
        ),
      ),
    );
  });

  public loadDividendYieldTotalByPosition$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(StatisticsActions.loadDividendYieldTotalByPosition),
      filter(() => !!this._authStateFacade.customerId),
      switchMap(({ isin }) =>
        this._statisticsInfrastructure.loadDividendYieldTotalByPosition(isin).pipe(
          map((dividendYieldTotal) => StatisticsApiActions.loadDividendYieldTotalByPositionSuccess({ dividendYieldTotal })),
          catchError((error: HttpErrorResponse) => of(StatisticsApiActions.loadDividendYieldTotalByPositionFailure({ error }))),
        ),
      ),
    );
  });

  public handleError$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(StatisticsApiActions.loadChartAndStatsFailure),
        tap((error) => this._storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );

  private sortInstrumentNameAsc = (dividends: Dividend[]): Dividend[] => {
    return dividends.sort((a, b) => (a.instrumentName > b.instrumentName ? 1 : -1));
  };

  private appendSelfcalculatedForecast = (dividends: Dividend[]): Dividend[] => {
    const maxDividendYearForecast = new Date().getFullYear() + STATS_DIVIDENDS_FORECAST_YEARS;

    dividends.forEach((dividend) => {
      if (!dividend?.forecastedPeriods?.length) {
        return;
      }
      dividend.forecastedPeriods.sort((a, b) => (a.period > b.period ? 1 : -1)); //sort period ascending

      const minPeriodNumber = Number(dividend.forecastedPeriods[0].period);
      if (isNaN(minPeriodNumber)) {
        return;
      }

      let lastExistingPeriod = null;
      for (let year = minPeriodNumber; year <= maxDividendYearForecast; year++) {
        const period = dividend.forecastedPeriods.find((forecastedPeriod) => forecastedPeriod.period === year.toString());
        if (period) {
          lastExistingPeriod = period;
          continue;
        }
        if (lastExistingPeriod?.dividendAbsolute) {
          const selfForecastPeriod: DividendPeriod = {
            period: year.toString(),
            dividendAbsolute: lastExistingPeriod.dividendAbsolute,
            selfForecasted: true,
          };
          dividend.forecastedPeriods.push(selfForecastPeriod);
        }
      }
    });
    return dividends;
  };
}
